// src/components/HRComponents/PolicyDocumentViewer.js
import React, { useEffect, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import mammoth from 'mammoth';
import { ChevronLeft, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';

// Import react-pdf core stylesheets to solve alignment, text duplication, and empty space issues
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';

// Set up secure HTTPS worker URL using the modern .mjs extension to avoid CORS redirect blocks
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// IMPORTANT: this component only PREVIEWS the document. It never modifies or
// re-saves the original file. PDFs are rendered natively via react-pdf/pdf.js.
// DOCX files are converted to HTML in-memory via mammoth.js purely for
// on-screen preview — the stored file on the server remains the original
// .docx, untouched. If your project already has an in-house viewer/converter,
// swap this component's internals for that instead — the outer contract
// (fileUrl + mimeType in -> rendered preview out) stays the same.
const PolicyDocumentViewer = ({ policyId, fileUrl, mimeType, fileName }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [docxHtml, setDocxHtml] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null); // Local state to store secure PDF blob URL
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isPdf = mimeType === 'application/pdf';
  const isDocx = mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    || mimeType === 'application/msword';

  useEffect(() => {
    let cancelled = false;
    let localBlobUrl = null; // Local variable to track active Blob URL for cleanup

    const loadDocument = async () => {
      try {
        setLoading(true);
        setError(null);

        // FIX: Appended '?preview=true' query parameter to bypass creating download logs on preview loads [1]
        const previewUrl = `${SummaryApi.downloadPolicy.url(policyId)}?preview=true`;

        if (isPdf) {
          // Fetch PDF secure blob using authenticated api client to prevent 401 Unauthorized
          const response = await api.get(previewUrl, { responseType: 'blob' });
          if (!cancelled) {
            localBlobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            setPdfBlobUrl(localBlobUrl);
          }
        } else if (isDocx) {
          // Fetch DOCX file as arraybuffer for mammoth conversion
          const response = await api.get(previewUrl, { responseType: 'arraybuffer' });
          const result = await mammoth.convertToHtml({ arrayBuffer: response.data });
          if (!cancelled) setDocxHtml(result.value);
        }
      } catch (err) {
        if (!cancelled) setError('Unable to preview this document. You can still download it.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDocument();

    // Cleanup block to revoke local Blob URLs and prevent memory leaks
    return () => {
      cancelled = true;
      if (localBlobUrl) {
        window.URL.revokeObjectURL(localBlobUrl);
      }
    };
  }, [policyId, isPdf, isDocx]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-gray-500">
        <AlertCircle className="h-8 w-8 mb-2 text-red-400" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  // Render Loader spinner while fetching PDF / DOCX
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isPdf && pdfBlobUrl) {
    return (
      <div className="flex flex-col items-center bg-gray-50 rounded-xl p-4">
        <Document
          file={pdfBlobUrl} // Secure Blob URL containing authorization token
          onLoadSuccess={({ numPages }) => setNumPages(numPages)}
          onLoadError={() => setError('Unable to load PDF preview.')}
          loading={<Loader2 className="h-6 w-6 animate-spin text-gray-400 my-8" />}
        >
          <Page pageNumber={pageNumber} width={700} />
        </Document>
        {numPages && (
          <div className="flex items-center gap-4 mt-4">
            <button
              disabled={pageNumber <= 1}
              onClick={() => setPageNumber((p) => p - 1)}
              className="p-2 rounded-lg border disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm text-gray-600">Page {pageNumber} of {numPages}</span>
            <button
              disabled={pageNumber >= numPages}
              onClick={() => setPageNumber((p) => p + 1)}
              className="p-2 rounded-lg border disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    );
  }

  if (isDocx) {
    return (
      <div
        className="prose prose-sm max-w-none bg-white rounded-xl border p-6 overflow-auto max-h-[70vh]"
        dangerouslySetInnerHTML={{ __html: docxHtml }}
      />
    );
  }

  return (
    <div className="text-center py-12 text-gray-500 text-sm">
      Preview not available for this file type ({fileName}). Please download to view.
    </div>
  );
};

export default PolicyDocumentViewer;